import { useCallback, useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

// Helper function to extract text from element
const getElementText = (element) => {
  if (!element) return '';
  return element.innerText || element.textContent || '';
};

// Helper function to extract text from multiple elements
const getElementsText = (elements) => {
  if (!elements || elements.length === 0) return '';
  return Array.from(elements)
    .map(el => el.innerText || el.textContent || '')
    .filter(text => text.trim())
    .join('. ');
};

// Get home page content in specific order using data attributes
const getHomePageContent = (isMarathi) => {
  const sections = [];

  // Define reading order with data attributes
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'whats-new', label: isMarathi() ? 'नवीन काय आहे' : 'What\'s New' },
    { attr: 'welcome-note', label: isMarathi() ? 'स्वागत नोट' : 'Welcome Note' },
    { attr: 'boards-work', label: isMarathi() ? 'मंडळाचे कार्य' : 'Board\'s Work' },
    { attr: 'leadership-info', label: isMarathi() ? 'नेतृत्व माहिती' : 'Leadership Information' },
    { attr: 'quick-links', label: isMarathi() ? 'त्वरित लिंक्स' : 'Quick Links' },
    { attr: 'important-circulars', label: isMarathi() ? 'महत्वाची परिपत्रके' : 'Important Circulars' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      // Special handling for different section types
      if (attr === 'navbar') {
        // Get all nav links text
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'whats-new') {
        // Get news label and items
        const newsLabel = element.querySelector('.news-label');
        const newsItems = element.querySelectorAll('.news-item');
        const labelText = newsLabel ? getElementText(newsLabel) : '';
        const itemsText = Array.from(newsItems)
          .slice(0, 3) // Only first 3 to avoid duplicates
          .map(item => getElementText(item))
          .filter(t => t.trim())
          .join('. ');
        text = [labelText, itemsText].filter(t => t).join('. ');
      } else if (attr === 'welcome-note') {
        // Get title and text
        const title = element.querySelector('.welcome-note-title');
        const textEl = element.querySelector('.welcome-note-text');
        const titleText = title ? getElementText(title) : '';
        const textContent = textEl ? getElementText(textEl) : '';
        text = [titleText, textContent].filter(t => t).join('. ');
      } else if (attr === 'boards-work') {
        // Get title and feature titles
        const title = element.querySelector('.features-title');
        const featureTitles = element.querySelectorAll('.feature-title');
        const titleText = title ? getElementText(title) : '';
        const featuresText = Array.from(featureTitles)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, featuresText].filter(t => t).join('. ');
      } else if (attr === 'leadership-info') {
        // Get all leadership cards
        const cards = element.querySelectorAll('.leadership-card');
        const cardsText = Array.from(cards).map(card => {
          const name = card.querySelector('.leadership-name');
          const designation = card.querySelector('.leadership-designation');
          const nameText = name ? getElementText(name) : '';
          const designationText = designation ? getElementText(designation) : '';
          return [nameText, designationText].filter(t => t).join(', ');
        }).filter(t => t).join('. ');
        text = cardsText;
      } else if (attr === 'quick-links') {
        // Get title and link texts
        const title = element.querySelector('.circulars-title');
        const linkTexts = element.querySelectorAll('.quick-link-text');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(linkTexts)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'important-circulars') {
        // Get title and link texts
        const title = element.querySelector('.circulars-title');
        const linkTexts = element.querySelectorAll('.quick-link-text');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(linkTexts)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-other-links') {
        // Get title and link texts
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        // Get title and link texts
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        // Get title
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        // Get visitor stats
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        // Get last updated text
        text = getElementText(element);
      } else {
        // Default: get all text from element
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get overview page content in specific order
const getOverviewPageContent = (isMarathi) => {
  const sections = [];

  // Define reading order for Overview page
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'overview-board-members', label: isMarathi() ? 'मंडळ सदस्य' : 'Board Members' },
    { attr: 'overview-section', label: isMarathi() ? 'आढावा' : 'Overview' },
    { attr: 'overview-introduction', label: isMarathi() ? 'परिचय' : 'Introduction' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      // Special handling for different section types
      if (attr === 'navbar') {
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'overview-board-members') {
        // Get all board member names and designations
        const cards = element.querySelectorAll('.board-member-card');
        const cardsText = Array.from(cards).map(card => {
          const name = card.querySelector('.board-member-name');
          const designation = card.querySelector('.board-member-designation');
          const nameText = name ? getElementText(name) : '';
          const designationText = designation ? getElementText(designation) : '';
          return [nameText, designationText].filter(t => t).join(', ');
        }).filter(t => t).join('. ');
        text = cardsText;
      } else if (attr === 'overview-section') {
        // Get title and all content
        const title = element.querySelector('.section-title');
        const content = element.querySelector('.section-content');
        const titleText = title ? getElementText(title) : '';
        const contentText = content ? getElementText(content) : '';
        text = [titleText, contentText].filter(t => t).join('. ');
      } else if (attr === 'overview-introduction') {
        // Get title and all content
        const title = element.querySelector('.section-title');
        const content = element.querySelector('.section-content');
        const titleText = title ? getElementText(title) : '';
        const contentText = content ? getElementText(content) : '';
        text = [titleText, contentText].filter(t => t).join('. ');
      } else if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      } else {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get administrative structure page content
const getAdministrativeStructureContent = (isMarathi) => {
  const sections = [];

  // Define reading order
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'admin-structure-content', label: isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content' },
    { attr: 'admin-structure-tree', label: isMarathi() ? 'संघटन रचना' : 'Organizational Structure' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'navbar') {
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'admin-structure-content') {
        // Get page description text
        text = getElementText(element);
      } else if (attr === 'admin-structure-tree') {
        // Read tree structure - use optimized Marathi text or read ARIA tree for English
        if (isMarathi()) {
          // Use optimized Marathi narration
          text = 'महाराष्ट्र कामगार कल्याण मंडळ – संघटन रचना. ';
          text += 'स्तर एक: कल्याण आयुक्त. ';
          text += 'स्तर दोन: अतिरिक्त कल्याण आयुक्त. ';
          text += 'स्तर तीन – अतिरिक्त कल्याण आयुक्त यांच्या अधिनस्त अधिकारी: ';
          text += '१. उप कल्याण आयुक्त – प्रशासन. प्रशासकीय अधिकारी. ';
          text += '२. उप कल्याण आयुक्त – कार्यक्रम. सहाय्यक कल्याण आयुक्त – कार्यक्रम व वाहन. ';
          text += '३. उप कल्याण आयुक्त – लेखा व लेखापरीक्षण. लेखा व लेखापरीक्षण अधिकारी. लेखापरीक्षण अधिकारी. लेखा अधिकारी. ';
          text += '४. थेट अधिनस्त अधिकारी: जनसंपर्क अधिकारी. सहाय्यक कल्याण आयुक्त – महसूल. स्थापत्य अभियंता. कायदे अधिकारी. सहाय्यक कल्याण आयुक्त – संगणक.';
        } else {
          // English version - read tree structure from ARIA tree
          const treeItems = element.querySelectorAll('[role="treeitem"]');
          const treeText = Array.from(treeItems)
            .map(item => getElementText(item))
            .filter(t => t.trim())
            .join('. ');
          text = treeText;
        }
      } else if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      } else {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get board project page content
const getBoardProjectContent = (isMarathi) => {
  const sections = [];

  // Define reading order
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'board-project-content', label: isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content' },
    { attr: 'board-project-table', label: isMarathi() ? 'केंद्र प्रकार सारणी' : 'Centre Types Table' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'navbar') {
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'board-project-content') {
        // Get title and all content
        const title = element.querySelector('.section-title');
        const content = element.querySelector('.section-content');
        const titleText = title ? getElementText(title) : '';
        const contentText = content ? getElementText(content) : '';
        text = [titleText, contentText].filter(t => t).join('. ');
      } else if (attr === 'board-project-table') {
        // Read table in a structured way
        const table = element.querySelector('.board-project-table');
        if (table) {
          // Get headers
          const headers = table.querySelectorAll('thead th');
          const headerTexts = Array.from(headers)
            .map(h => getElementText(h))
            .filter(t => t.trim())
            .join(', ');
          
          // Get rows
          const rows = table.querySelectorAll('tbody tr');
          const rowsText = Array.from(rows).map(row => {
            const cells = row.querySelectorAll('td');
            const cellTexts = Array.from(cells)
              .map(cell => getElementText(cell))
              .filter(t => t.trim());
            return cellTexts.join(', ');
          }).filter(t => t).join('. ');
          
          text = [headerTexts, rowsText].filter(t => t).join('. ');
        }
      } else if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      } else {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get board members page content
const getBoardMembersContent = (isMarathi) => {
  const sections = [];

  // Define reading order
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'board-members-content', label: isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content' },
    { attr: 'board-members-table', label: isMarathi() ? 'मंडळ सदस्य सारणी' : 'Board Members Table' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'navbar') {
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'board-members-content') {
        // Get page header and any other content in this section
        // The PageHeader component might not have data-read-section, so we get all text
        text = getElementText(element);
      } else if (attr === 'board-members-table') {
        // Read table in a structured format
        const table = element.querySelector('.board-members-table');
        if (table) {
          // Get headers
          const headers = table.querySelectorAll('thead th');
          const headerTexts = Array.from(headers)
            .map(h => getElementText(h))
            .filter(t => t.trim())
            .join(', ');
          
          // Get rows with formatted output
          const rows = table.querySelectorAll('tbody tr');
          const rowsText = Array.from(rows).map((row, index) => {
            const serial = row.querySelector('.board-member-serial');
            const details = row.querySelector('.board-member-details');
            const circularLink = row.querySelector('.board-member-link');
            const date = row.querySelector('.board-member-date');
            
            const serialText = serial ? getElementText(serial) : '';
            const detailsText = details ? getElementText(details) : '';
            const circularText = circularLink 
              ? (circularLink.getAttribute('title') || (isMarathi() ? 'परिपत्रक उपलब्ध' : 'Circular available'))
              : (isMarathi() ? 'परिपत्रक उपलब्ध नाही' : 'Circular not available');
            const dateText = date ? getElementText(date) : '';
            
            // Format: "Row X: Serial No: ... / Details: ... / Circular: ... / Date: ..."
            const parts = [];
            if (serialText) {
              parts.push(`${isMarathi() ? 'अनुक्रमांक' : 'Serial No'}: ${serialText}`);
            }
            if (detailsText) {
              parts.push(`${isMarathi() ? 'तपशील' : 'Details'}: ${detailsText}`);
            }
            if (circularText) {
              parts.push(`${isMarathi() ? 'परिपत्रक' : 'Circular'}: ${circularText}`);
            }
            if (dateText) {
              parts.push(`${isMarathi() ? 'दिनांक' : 'Date'}: ${dateText}`);
            }
            
            return parts.length > 0 ? parts.join('. ') : '';
          }).filter(t => t).join('. ');
          
          text = [headerTexts, rowsText].filter(t => t).join('. ');
        }
      } else if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      } else {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get annual report page content
const getAnnualReportContent = (isMarathi) => {
  const sections = [];

  // Define reading order
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'annual-report-content', label: isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'navbar') {
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'annual-report-content') {
        // Get main title and all report links
        const title = element.querySelector('.annual-report-main-title');
        const reportLinks = element.querySelectorAll('.annual-report-link');
        
        const titleText = title ? getElementText(title) : '';
        const reportsText = Array.from(reportLinks).map(link => {
          const linkTitle = link.querySelector('.annual-report-title');
          const linkTitleText = linkTitle ? getElementText(linkTitle) : '';
          const ariaLabel = link.getAttribute('aria-label') || '';
          // Use aria-label if available, otherwise use title text
          return ariaLabel || linkTitleText;
        }).filter(t => t).join('. ');
        
        text = [titleText, reportsText].filter(t => t).join('. ');
      } else if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      } else {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get circulars for employees page content
const getCircularsContent = (isMarathi) => {
  const sections = [];

  // Define reading order
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'circulars-content', label: isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'navbar') {
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'circulars-content') {
        // Get page header and table content
        // The PageHeader component text will be included in the element's text
        // Get table content separately
        const table = element.querySelector('.circulars-for-employees-table');
        let tableText = '';
        
        if (table) {
          // Get headers
          const headers = table.querySelectorAll('thead th');
          const headerTexts = Array.from(headers)
            .map(h => getElementText(h))
            .filter(t => t.trim())
            .join(', ');
          
          // Get rows with formatted output
          const rows = table.querySelectorAll('tbody tr');
          const rowsText = Array.from(rows).map((row) => {
            const serial = row.querySelector('.circular-for-employee-serial');
            const details = row.querySelector('.circular-for-employee-details');
            const circularLink = row.querySelector('.circular-for-employee-link');
            
            const serialText = serial ? getElementText(serial) : '';
            const detailsText = details ? getElementText(details) : '';
            const circularText = circularLink 
              ? (circularLink.getAttribute('title') || (isMarathi() ? 'परिपत्रक उपलब्ध' : 'Circular available'))
              : (isMarathi() ? 'परिपत्रक उपलब्ध नाही' : 'Circular not available');
            
            // Format: "Serial No: ... / Details: ... / Circular: ..."
            const parts = [];
            if (serialText) {
              parts.push(`${isMarathi() ? 'अनुक्रमांक' : 'Serial No'}: ${serialText}`);
            }
            if (detailsText) {
              parts.push(`${isMarathi() ? 'तपशील' : 'Details'}: ${detailsText}`);
            }
            if (circularText) {
              parts.push(`${isMarathi() ? 'परिपत्रक' : 'Circular'}: ${circularText}`);
            }
            
            return parts.length > 0 ? parts.join('. ') : '';
          }).filter(t => t).join('. ');
          
          tableText = [headerTexts, rowsText].filter(t => t).join('. ');
        }
        
        // Get all text from the content element (includes page header)
        const allText = getElementText(element);
        
        // Combine page header text with table text
        text = [allText, tableText].filter(t => t).join('. ');
      } else if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      } else {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get workers welfare fund page content
const getWorkersWelfareContent = (isMarathi) => {
  const sections = [];

  // Define reading order
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'workers-welfare-content', label: isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'navbar') {
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'workers-welfare-content') {
        // Get all content including page header, sections, tables, and form links
        // The PageHeader component text will be included in the element's text
        const sections = element.querySelectorAll('.welfare-section');
        const sectionsText = Array.from(sections).map(section => {
          const title = section.querySelector('.section-title');
          const content = section.querySelector('.section-content');
          
          const titleText = title ? getElementText(title) : '';
          let contentText = '';
          
          if (content) {
            // Get text content
            const textContent = content.querySelector('.section-text');
            if (textContent) {
              // Get all text including tables
              contentText = getElementText(textContent);
            }
            
            // Get form links if available
            const formLinks = content.querySelectorAll('.quick-link-item');
            if (formLinks.length > 0) {
              const linksText = Array.from(formLinks).map(link => {
                const linkText = link.querySelector('.quick-link-text');
                return linkText ? getElementText(linkText) : '';
              }).filter(t => t).join(', ');
              contentText = [contentText, linksText].filter(t => t).join('. ');
            }
          }
          
          return [titleText, contentText].filter(t => t).join('. ');
        }).filter(t => t).join('. ');
        
        // Get page header text (it's in the parent container)
        const allText = getElementText(element);
        text = allText || sectionsText;
      } else if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      } else {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get right to information page content
const getRightToInfoContent = (isMarathi) => {
  const sections = [];

  // Define reading order
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'right-to-info-content', label: isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'navbar') {
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'right-to-info-content') {
        // Get main title, description, and table content
        const title = element.querySelector('.right-to-info-main-title');
        const description = element.querySelector('.right-to-info-description');
        const table = element.querySelector('.officers-table');
        
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        let tableText = '';
        
        if (table) {
          // Get headers
          const headers = table.querySelectorAll('thead th');
          const headerTexts = Array.from(headers)
            .map(h => getElementText(h))
            .filter(t => t.trim())
            .join(', ');
          
          // Get rows with formatted output
          const rows = table.querySelectorAll('tbody tr');
          const rowsText = Array.from(rows).map((row) => {
            const srNo = row.querySelector('.sr-no');
            const jurisdiction = row.querySelector('.jurisdiction');
            const address = row.querySelector('.address');
            const designation = row.querySelector('.designation');
            const phone = row.querySelector('.phone');
            
            const srNoText = srNo ? getElementText(srNo) : '';
            const jurisdictionText = jurisdiction ? getElementText(jurisdiction) : '';
            const addressText = address ? getElementText(address) : '';
            const designationText = designation ? getElementText(designation) : '';
            const phoneText = phone ? getElementText(phone) : '';
            
            // Format: "Row X: Sr. No: ... / Jurisdiction: ... / Address: ... / Designation: ... / Phone: ..."
            const parts = [];
            if (srNoText) {
              parts.push(`${isMarathi() ? 'अनुक्रमांक' : 'Serial No'}: ${srNoText}`);
            }
            if (jurisdictionText) {
              parts.push(`${isMarathi() ? 'कार्यक्षेत्र' : 'Jurisdiction'}: ${jurisdictionText}`);
            }
            if (addressText) {
              parts.push(`${isMarathi() ? 'पत्ता' : 'Address'}: ${addressText}`);
            }
            if (designationText) {
              parts.push(`${isMarathi() ? 'पदनाम' : 'Designation'}: ${designationText}`);
            }
            if (phoneText) {
              parts.push(`${isMarathi() ? 'दूरध्वनी क्रमांक' : 'Phone Number'}: ${phoneText}`);
            }
            
            return parts.length > 0 ? parts.join('. ') : '';
          }).filter(t => t).join('. ');
          
          tableText = [headerTexts, rowsText].filter(t => t).join('. ');
        }
        
        // Combine all text
        text = [titleText, descText, tableText].filter(t => t).join('. ');
      } else if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      } else {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get right to service page content
const getRightToServiceContent = (isMarathi) => {
  const sections = [];

  // Define reading order
  const readingOrder = [
    { attr: 'navbar', label: isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu' },
    { attr: 'right-to-service-content', label: isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content' },
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  // Read each section in order
  readingOrder.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'navbar') {
        const navLinks = element.querySelectorAll('.nav-link, .nav-dropdown-link');
        const linksText = getElementsText(navLinks);
        text = linksText || getElementText(element);
      } else if (attr === 'right-to-service-content') {
        // Get page header and table content
        // The PageHeader component text will be included in the element's text
        // Get table content separately
        const table = element.querySelector('.right-to-service-table');
        let tableText = '';
        
        if (table) {
          // Get headers
          const headers = table.querySelectorAll('thead th');
          const headerTexts = Array.from(headers)
            .map(h => getElementText(h))
            .filter(t => t.trim())
            .join(', ');
          
          // Get rows with formatted output
          const rows = table.querySelectorAll('tbody tr');
          const rowsText = Array.from(rows).map((row) => {
            const serial = row.querySelector('.right-to-service-serial');
            const details = row.querySelector('.right-to-service-details');
            const circularLink = row.querySelector('.right-to-service-link');
            const date = row.querySelector('.right-to-service-na, .right-to-service-date');
            
            const serialText = serial ? getElementText(serial) : '';
            const detailsText = details ? getElementText(details) : '';
            const circularText = circularLink 
              ? (circularLink.getAttribute('title') || (isMarathi() ? 'परिपत्रक उपलब्ध' : 'Circular available'))
              : (isMarathi() ? 'परिपत्रक उपलब्ध नाही' : 'Circular not available');
            const dateText = date ? getElementText(date) : '';
            
            // Format: "Serial No: ... / Details: ... / Circular: ... / Date: ..."
            const parts = [];
            if (serialText) {
              parts.push(`${isMarathi() ? 'अनुक्रमांक' : 'Serial No'}: ${serialText}`);
            }
            if (detailsText) {
              parts.push(`${isMarathi() ? 'तपशील' : 'Details'}: ${detailsText}`);
            }
            if (circularText) {
              parts.push(`${isMarathi() ? 'परिपत्रक' : 'Circular'}: ${circularText}`);
            }
            if (dateText) {
              parts.push(`${isMarathi() ? 'दिनांक' : 'Date'}: ${dateText}`);
            }
            
            return parts.length > 0 ? parts.join('. ') : '';
          }).filter(t => t).join('. ');
          
          tableText = [headerTexts, rowsText].filter(t => t).join('. ');
        }
        
        // Get all text from the content element (includes page header)
        const allText = getElementText(element);
        
        // Combine page header text with table text
        text = [allText, tableText].filter(t => t).join('. ');
      } else if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      } else {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get tenders notices page content (only currently visible year)
const getTendersNoticesContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Skip general page content - only read selected year and its table
  const contentElement = document.querySelector('[data-read-section="tenders-notices-content"]');
  if (contentElement) {
    // Find the currently active/selected year
    const activeYearButton = contentElement.querySelector('.year-filter-item.active');
    const selectedYear = activeYearButton ? activeYearButton.textContent.trim() : '';
    
    // Get the currently visible table (only one table is visible at a time)
    const table = contentElement.querySelector('.tenders-notices-table');
    let tableText = '';
    
    if (table) {
      // Get headers
      const headers = table.querySelectorAll('thead th');
      const headerTexts = Array.from(headers)
        .map(h => getElementText(h))
        .filter(t => t.trim())
        .join(', ');
      
      // Get rows from the currently visible table
      const rows = table.querySelectorAll('tbody tr');
      const rowsText = Array.from(rows).map((row) => {
        // Check if this is the "no data" row
        const noDataCell = row.querySelector('.no-data');
        if (noDataCell) {
          return getElementText(noDataCell);
        }
        
        // Get regular row data
        const details = row.querySelector('.tender-details');
        const link = row.querySelector('.tender-link');
        const publishDate = row.querySelector('.tender-publish-date');
        const lastDate = row.querySelector('.tender-last-date');
        
        const detailsText = details ? getElementText(details) : '';
        const linkText = link 
          ? (link.getAttribute('aria-label') || (isMarathi() ? 'अधिक माहिती उपलब्ध' : 'More information available'))
          : '';
        const publishDateText = publishDate ? getElementText(publishDate) : '';
        const lastDateText = lastDate ? getElementText(lastDate) : '';
        
        // Format: "Details: ... / More Info: ... / Publication Date: ... / Last Date: ..."
        const parts = [];
        if (detailsText) {
          parts.push(`${isMarathi() ? 'तपशील' : 'Details'}: ${detailsText}`);
        }
        if (linkText) {
          parts.push(`${isMarathi() ? 'अधिक माहिती' : 'More Information'}: ${linkText}`);
        }
        if (publishDateText) {
          parts.push(`${isMarathi() ? 'प्रसिद्धी दिनांक' : 'Publication Date'}: ${publishDateText}`);
        }
        if (lastDateText) {
          parts.push(`${isMarathi() ? 'अंतिम दिनांक' : 'Last Date'}: ${lastDateText}`);
        }
        
        return parts.length > 0 ? parts.join('. ') : '';
      }).filter(t => t).join('. ');
      
      // Add year information if available
      const yearLabel = selectedYear 
        ? (isMarathi() ? `वर्ष ${selectedYear}` : `Year ${selectedYear}`)
        : '';
      
      tableText = [yearLabel, headerTexts, rowsText].filter(t => t).join('. ');
    }
    
    if (tableText.trim()) {
      sections.push(isMarathi() ? 'निविदा आणि सूचना' : 'Tenders and Notices');
      sections.push(tableText.trim());
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get list of services page content (skip general page content, only read table)
const getListOfServicesContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Skip general page content - only read table content
  const contentElement = document.querySelector('[data-read-section="list-of-services-content"]');
  if (contentElement) {
    // Find the schemes table
    const table = contentElement.querySelector('.schemes-table');
    let tableText = '';
    
    if (table) {
      // Get headers
      const headers = table.querySelectorAll('thead th');
      const headerTexts = Array.from(headers)
        .map(h => getElementText(h))
        .filter(t => t.trim())
        .join(', ');
      
      // Get rows from the table
      const rows = table.querySelectorAll('tbody tr');
      const rowsText = Array.from(rows).map((row) => {
        const cells = row.querySelectorAll('td');
        const serial = cells[0]?.querySelector('.scheme-serial') || cells[0];
        const details = cells[1]?.querySelector('.scheme-details') || cells[1];
        const circularCell = cells[2];
        const formCell = cells[3];
        const date = cells[4]?.querySelector('.scheme-date') || cells[4];
        
        const serialText = serial ? getElementText(serial) : '';
        const detailsText = details ? getElementText(details) : '';
        
        // Get circular link text
        const circularLink = circularCell?.querySelector('a');
        const circularText = circularLink 
          ? (circularLink.getAttribute('title') || (isMarathi() ? 'परिपत्रक उपलब्ध' : 'Circular available'))
          : '';
        
        // Get form link text or NA
        const formLink = formCell?.querySelector('a');
        const naText = formCell?.querySelector('.scheme-na');
        const formText = formLink 
          ? (formLink.getAttribute('title') || (isMarathi() ? 'फॉर्म उपलब्ध' : 'Form available'))
          : (naText ? 'NA' : '');
        
        const dateText = date ? getElementText(date) : '';
        
        // Format: "Serial: ... / Scheme: ... / Circular: ... / Form: ... / Last Date: ..."
        const parts = [];
        if (serialText) {
          parts.push(`${isMarathi() ? 'अनुक्रमांक' : 'Serial No.'}: ${serialText}`);
        }
        if (detailsText) {
          parts.push(`${isMarathi() ? 'योजना' : 'Scheme'}: ${detailsText}`);
        }
        if (circularText) {
          parts.push(`${isMarathi() ? 'परिपत्रक' : 'Circular'}: ${circularText}`);
        }
        if (formText) {
          parts.push(`${isMarathi() ? 'फॉर्म' : 'Form'}: ${formText}`);
        }
        if (dateText) {
          parts.push(`${isMarathi() ? 'अंतिम तारीख' : 'Last Date'}: ${dateText}`);
        }
        
        return parts.length > 0 ? parts.join('. ') : '';
      }).filter(t => t).join('. ');
      
      tableText = [headerTexts, rowsText].filter(t => t).join('. ');
    }
    
    if (tableText.trim()) {
      sections.push(isMarathi() ? 'सेवांची यादी' : 'List of Services');
      sections.push(tableText.trim());
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get feedback page content
const getFeedbackContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Read page content
  const contentElement = document.querySelector('[data-read-section="feedback-content"]');
  if (contentElement) {
    let pageText = '';
    
    // Read feedback form section
    const formSection = contentElement.querySelector('.feedback-section');
    if (formSection) {
      const sectionTitle = formSection.querySelector('.section-title');
      const titleText = sectionTitle ? getElementText(sectionTitle) : '';
      
      // Read form labels and structure
      const formGroups = formSection.querySelectorAll('.form-group');
      const formFieldsText = Array.from(formGroups).map(group => {
        const label = group.querySelector('.form-label');
        if (!label) return '';
        
        let labelText = getElementText(label);
        // Remove asterisks and optional text for cleaner reading
        labelText = labelText.replace(/\*/g, '').replace(/\([^)]*\)/g, '').trim();
        
        // Check if this is the rating group
        const ratingStars = group.querySelector('.rating-stars');
        if (ratingStars) {
          labelText += (isMarathi() ? '. 5 स्टार रेटिंग प्रणाली' : '. 5 star rating system');
        }
        
        // Check if this is the quick reactions group
        const quickReactions = group.querySelector('.quick-reactions');
        if (quickReactions) {
          labelText += (isMarathi() ? '. त्वरित प्रतिक्रिया पर्याय उपलब्ध' : '. Quick reaction options available');
        }
        
        // Check if this is the file attachment group
        const fileInfo = group.querySelector('.file-info');
        if (fileInfo) {
          const fileInfoText = getElementText(fileInfo);
          labelText += fileInfoText ? `. ${fileInfoText}` : '';
        }
        
        return labelText;
      }).filter(t => t).join('. ');
      
      const formParts = [titleText, formFieldsText]
        .filter(t => t)
        .join('. ');
      
      if (formParts) {
        pageText += formParts;
      }
    }
    
    // Read important information section
    const infoSection = contentElement.querySelectorAll('.feedback-section')[1];
    if (infoSection) {
      const infoTitle = infoSection.querySelector('.section-title');
      const titleText = infoTitle ? getElementText(infoTitle) : '';
      
      const infoItems = infoSection.querySelectorAll('.info-item');
      const itemsText = Array.from(infoItems).map(item => {
        const infoTitle = item.querySelector('.info-title');
        const infoText = item.querySelector('.info-text');
        const title = infoTitle ? getElementText(infoTitle) : '';
        const text = infoText ? getElementText(infoText) : '';
        return title && text ? `${title}: ${text}` : '';
      }).filter(t => t).join('. ');
      
      const infoParts = [titleText, itemsText].filter(t => t).join('. ');
      if (infoParts) {
        pageText += (pageText ? '. ' : '') + infoParts;
      }
    }
    
    if (pageText.trim()) {
      sections.push(isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content');
      sections.push(pageText.trim());
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get grievance redressal page content
const getGrievanceRedressalContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Read page content
  const contentElement = document.querySelector('[data-read-section="grievance-content"]');
  if (contentElement) {
    let pageText = '';
    
    // Read grievance form section
    const formSection = contentElement.querySelector('.grievance-section');
    if (formSection) {
      const sectionTitle = formSection.querySelector('.section-title');
      const titleText = sectionTitle ? getElementText(sectionTitle) : '';
      
      // Read form labels and structure
      const formGroups = formSection.querySelectorAll('.form-group');
      const formFieldsText = Array.from(formGroups).map(group => {
        const label = group.querySelector('.form-label');
        if (!label) return '';
        
        let labelText = getElementText(label);
        // Remove asterisks and optional text for cleaner reading
        labelText = labelText.replace(/\*/g, '').replace(/\([^)]*\)/g, '').trim();
        
        // Check if this is the file attachment group
        const fileInfo = group.querySelector('.file-info');
        if (fileInfo) {
          const fileInfoText = getElementText(fileInfo);
          labelText += fileInfoText ? `. ${fileInfoText}` : '';
        }
        
        return labelText;
      }).filter(t => t).join('. ');
      
      const formParts = [titleText, formFieldsText]
        .filter(t => t)
        .join('. ');
      
      if (formParts) {
        pageText += formParts;
      }
    }
    
    // Read important information section
    const infoSection = contentElement.querySelectorAll('.grievance-section')[1];
    if (infoSection) {
      const infoTitle = infoSection.querySelector('.section-title');
      const titleText = infoTitle ? getElementText(infoTitle) : '';
      
      const infoItems = infoSection.querySelectorAll('.info-item');
      const itemsText = Array.from(infoItems).map(item => {
        const infoTitle = item.querySelector('.info-title');
        const infoText = item.querySelector('.info-text');
        const title = infoTitle ? getElementText(infoTitle) : '';
        const text = infoText ? getElementText(infoText) : '';
        return title && text ? `${title}: ${text}` : '';
      }).filter(t => t).join('. ');
      
      const infoParts = [titleText, itemsText].filter(t => t).join('. ');
      if (infoParts) {
        pageText += (pageText ? '. ' : '') + infoParts;
      }
    }
    
    if (pageText.trim()) {
      sections.push(isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content');
      sections.push(pageText.trim());
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get FAQ page content
const getFAQContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Read page content
  const contentElement = document.querySelector('[data-read-section="faq-content"]');
  if (contentElement) {
    let pageText = '';
    
    // Read all FAQ items (questions and answers)
    const faqItems = contentElement.querySelectorAll('.faq-item');
    const faqTexts = Array.from(faqItems).map(item => {
      const question = item.querySelector('.faq-question-text');
      const answer = item.querySelector('.faq-answer-content p');
      const questionText = question ? getElementText(question) : '';
      const answerText = answer ? getElementText(answer) : '';
      return questionText && answerText ? `${questionText}. ${answerText}` : '';
    }).filter(t => t);
    
    if (faqTexts.length > 0) {
      pageText = faqTexts.join('. ');
    }
    
    // Read FAQ footer note
    const faqFooter = document.querySelector('.faq-footer');
    if (faqFooter) {
      const faqNote = faqFooter.querySelector('.faq-note');
      const noteText = faqNote ? getElementText(faqNote) : '';
      if (noteText) {
        pageText += (pageText ? '. ' : '') + noteText;
      }
    }
    
    if (pageText.trim()) {
      sections.push(isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content');
      sections.push(pageText.trim());
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get 100 Days Program page content
const get100DaysProgramContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Read page content
  const contentElement = document.querySelector('[data-read-section="hundred-days-program-content"]');
  if (contentElement) {
    let pageText = '';
    
    // Read program section
    const programSection = contentElement.querySelector('.program-section');
    if (programSection) {
      const sectionTitle = programSection.querySelector('.section-title');
      const sectionText = programSection.querySelector('.section-text');
      const titleText = sectionTitle ? getElementText(sectionTitle) : '';
      const textContent = sectionText ? getElementText(sectionText) : '';
      const contentParts = [titleText, textContent].filter(t => t).join('. ');
      if (contentParts) {
        pageText += contentParts;
      }
    }
    
    if (pageText.trim()) {
      sections.push(isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content');
      sections.push(pageText.trim());
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get Contact Us page content
const getContactUsContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Read page content
  const contentElement = document.querySelector('[data-read-section="contact-content"]');
  if (contentElement) {
    let pageText = '';
    
    // Read hero section title
    const heroTitle = contentElement.querySelector('.hero-title');
    const titleText = heroTitle ? getElementText(heroTitle) : '';
    if (titleText) {
      pageText += titleText;
    }
    
    // Read all quick contact cards
    const quickCards = contentElement.querySelectorAll('.quick-card');
    const cardsText = Array.from(quickCards).map(card => {
      const cardContent = card.querySelector('.quick-card-content');
      if (!cardContent) return '';
      
      const officeName = cardContent.querySelector('h3');
      const address = cardContent.querySelector('.quick-card-address');
      const phoneBtn = cardContent.querySelector('.phone-btn');
      const emailBtn = cardContent.querySelector('.email-btn');
      
      const nameText = officeName ? getElementText(officeName) : '';
      const addressText = address ? getElementText(address) : '';
      const phoneText = phoneBtn ? getElementText(phoneBtn) : '';
      const emailText = emailBtn ? getElementText(emailBtn) : '';
      
      const parts = [nameText, addressText, phoneText, emailText].filter(t => t);
      return parts.length > 0 ? parts.join('. ') : '';
    }).filter(t => t);
    
    if (cardsText.length > 0) {
      const cardsContent = cardsText.join('. ');
      pageText += (pageText ? '. ' : '') + cardsContent;
    }
    
    if (pageText.trim()) {
      sections.push(isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content');
      sections.push(pageText.trim());
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get Site Map page content
const getSiteMapContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Read page content (includes main sitemap section and external links)
  const contentElements = document.querySelectorAll('[data-read-section="sitemap-content"]');
  if (contentElements.length > 0) {
    let pageText = '';
    
    contentElements.forEach((contentElement) => {
      // Read main title (from main section)
      const mainTitle = contentElement.querySelector('.sitemap-main-title');
      if (mainTitle) {
        const titleText = getElementText(mainTitle);
        if (titleText) {
          pageText += (pageText ? '. ' : '') + titleText;
        }
      }
      
      // Read all columns (from main section)
      const columns = contentElement.querySelectorAll('.sitemap-column');
      if (columns.length > 0) {
        const columnsText = Array.from(columns).map(column => {
          const columnTitle = column.querySelector('.sitemap-column-title');
          const links = column.querySelectorAll('.sitemap-link');
          
          const titleText = columnTitle ? getElementText(columnTitle) : '';
          const linksText = Array.from(links)
            .map(link => getElementText(link))
            .filter(t => t.trim())
            .join(', ');
          
          return titleText && linksText ? `${titleText}: ${linksText}` : '';
        }).filter(t => t);
        
        if (columnsText.length > 0) {
          const columnsContent = columnsText.join('. ');
          pageText += (pageText ? '. ' : '') + columnsContent;
        }
      }
      
      // Read external links section
      const externalTitle = contentElement.querySelector('.sitemap-external-title');
      const externalLinks = contentElement.querySelectorAll('.sitemap-external-link');
      
      if (externalTitle || externalLinks.length > 0) {
        const titleText = externalTitle ? getElementText(externalTitle) : '';
        const linksText = Array.from(externalLinks)
          .map(link => getElementText(link))
          .filter(t => t.trim())
          .join(', ');
        
        if (titleText && linksText) {
          pageText += (pageText ? '. ' : '') + `${titleText}: ${linksText}`;
        } else if (linksText) {
          pageText += (pageText ? '. ' : '') + linksText;
        }
      }
    });
    
    if (pageText.trim()) {
      sections.push(isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content');
      sections.push(pageText.trim());
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get Privacy Policy page content
const getPrivacyPolicyContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Read page content
  const contentElement = document.querySelector('[data-read-section="privacy-policy-content"]');
  if (contentElement) {
    const sectionsList = contentElement.querySelectorAll('.privacy-policy-section');
    const sectionsText = Array.from(sectionsList).map(section => {
      const title = section.querySelector('.privacy-policy-section-title');
      const answer = section.querySelector('.privacy-policy-section-answer');
      
      const titleText = title ? getElementText(title) : '';
      const answerText = answer ? getElementText(answer) : '';
      
      return titleText && answerText ? `${titleText}. ${answerText}` : '';
    }).filter(t => t);
    
    if (sectionsText.length > 0) {
      sections.push(isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content');
      sections.push(sectionsText.join('. '));
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get Screen Reader Support page content
const getScreenReaderContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Read page content
  const contentElement = document.querySelector('[data-read-section="screen-reader-content"]');
  if (contentElement) {
    const sectionsList = contentElement.querySelectorAll('.screen-reader-section');
    const sectionsText = Array.from(sectionsList).map(section => {
      let sectionText = '';
      
      // Read section title
      const title = section.querySelector('.screen-reader-section-title');
      const titleText = title ? getElementText(title) : '';
      if (titleText) {
        sectionText += titleText;
      }
      
      // Read paragraph if exists
      const paragraph = section.querySelector('.screen-reader-paragraph');
      if (paragraph) {
        const paraText = getElementText(paragraph);
        if (paraText) {
          sectionText += (sectionText ? '. ' : '') + paraText;
        }
      }
      
      // Read list items if exists
      const listItems = section.querySelectorAll('.screen-reader-list-item');
      if (listItems.length > 0) {
        const itemsText = Array.from(listItems)
          .map(item => getElementText(item))
          .filter(t => t.trim())
          .join('. ');
        if (itemsText) {
          sectionText += (sectionText ? '. ' : '') + itemsText;
        }
      }
      
      // Read table if exists
      const table = section.querySelector('.screen-reader-table');
      if (table) {
        // Get headers
        const headers = table.querySelectorAll('thead th');
        const headerTexts = Array.from(headers)
          .map(h => getElementText(h))
          .filter(t => t.trim())
          .join(', ');
        
        // Get rows
        const rows = table.querySelectorAll('tbody tr');
        const rowsText = Array.from(rows).map(row => {
          const cells = row.querySelectorAll('td');
          const cellTexts = Array.from(cells)
            .map(cell => getElementText(cell))
            .filter(t => t.trim());
          return cellTexts.join(', ');
        }).filter(t => t).join('. ');
        
        const tableText = [headerTexts, rowsText].filter(t => t).join('. ');
        if (tableText) {
          sectionText += (sectionText ? '. ' : '') + tableText;
        }
      }
      
      return sectionText;
    }).filter(t => t);
    
    if (sectionsText.length > 0) {
      sections.push(isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content');
      sections.push(sectionsText.join('. '));
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

// Get Contact Us - SOP page content
const getContactSOPContent = (isMarathi) => {
  const sections = [];

  // Read navbar first
  const navbarElement = document.querySelector('[data-read-section="navbar"]');
  if (navbarElement) {
    const navLinks = navbarElement.querySelectorAll('.nav-link, .nav-dropdown-link');
    const linksText = getElementsText(navLinks);
    const text = linksText || getElementText(navbarElement);
    if (text.trim()) {
      sections.push(isMarathi() ? 'नेव्हिगेशन मेनू' : 'Navigation Menu');
      sections.push(text.trim());
    }
  }

  // Read page content
  const contentElements = document.querySelectorAll('[data-read-section="contact-sop-content"]');
  if (contentElements.length > 0) {
    let pageText = '';
    
    contentElements.forEach((contentElement) => {
      // Check if it's customer support section
      const supportSection = contentElement.querySelector('.customer-support-section');
      if (supportSection) {
        const supportTitle = supportSection.querySelector('.support-title');
        const titleText = supportTitle ? getElementText(supportTitle) : '';
        
        const supportItems = supportSection.querySelectorAll('.support-item');
        const itemsText = Array.from(supportItems).map(item => {
          const label = item.querySelector('.support-label');
          const value = item.querySelector('.support-value, .support-action');
          const labelText = label ? getElementText(label) : '';
          const valueText = value ? getElementText(value) : '';
          return labelText && valueText ? `${labelText} ${valueText}` : '';
        }).filter(t => t).join('. ');
        
        if (titleText || itemsText) {
          pageText += (pageText ? '. ' : '') + [titleText, itemsText].filter(t => t).join('. ');
        }
      }
      
      // Check if it's directory section
      const directorySection = contentElement.querySelector('.directory-section');
      if (directorySection) {
        const directoryTitle = directorySection.querySelector('#directory-title');
        const titleText = directoryTitle ? getElementText(directoryTitle) : '';
        
        // Read table
        const table = directorySection.querySelector('.officials-table');
        let tableText = '';
        
        if (table) {
          // Get main title from table
          const mainTitle = table.querySelector('.main-title');
          const mainSubtitle = table.querySelector('.main-subtitle');
          const mainTitleText = mainTitle ? getElementText(mainTitle) : '';
          const mainSubtitleText = mainSubtitle ? getElementText(mainSubtitle) : '';
          
          // Get headers
          const headers = table.querySelectorAll('thead th');
          const headerTexts = Array.from(headers)
            .map(h => getElementText(h))
            .filter(t => t.trim())
            .join(', ');
          
          // Get rows
          const rows = table.querySelectorAll('tbody tr');
          const rowsText = Array.from(rows).map((row, index) => {
            // Skip header rows
            if (row.classList.contains('table-divisional-main') || row.querySelector('th')) {
              const divisionalTitle = row.querySelector('.divisional-main-title');
              const divisionalSubtitle = row.querySelector('.divisional-main-subtitle');
              if (divisionalTitle || divisionalSubtitle) {
                const divTitleText = divisionalTitle ? getElementText(divisionalTitle) : '';
                const divSubtitleText = divisionalSubtitle ? getElementText(divisionalSubtitle) : '';
                return [divTitleText, divSubtitleText].filter(t => t).join('. ');
              }
              return '';
            }
            
            const cells = row.querySelectorAll('td');
            const cellTexts = Array.from(cells)
              .map(cell => {
                // Get text from buttons or spans
                const button = cell.querySelector('button');
                if (button) {
                  const buttonText = button.querySelector('.phone-text, .email-text');
                  return buttonText ? getElementText(buttonText) : getElementText(button);
                }
                return getElementText(cell);
              })
              .filter(t => t.trim());
            return cellTexts.length > 0 ? `Row ${index + 1}: ${cellTexts.join(', ')}` : '';
          }).filter(t => t).join('. ');
          
          const tableParts = [];
          if (mainTitleText) tableParts.push(mainTitleText);
          if (mainSubtitleText) tableParts.push(mainSubtitleText);
          if (headerTexts) tableParts.push(`Headers: ${headerTexts}`);
          if (rowsText) tableParts.push(rowsText);
          
          tableText = tableParts.join('. ');
        }
        
        if (titleText || tableText) {
          pageText += (pageText ? '. ' : '') + [titleText, tableText].filter(t => t).join('. ');
        }
      }
      
      // Check if it's office hours section
      const hoursSection = contentElement.querySelector('.hours-section');
      if (hoursSection) {
        const hoursTitle = hoursSection.querySelector('#hours-title');
        const titleText = hoursTitle ? getElementText(hoursTitle) : '';
        
        const timingItems = hoursSection.querySelectorAll('.timing-item');
        const timingsText = Array.from(timingItems).map(item => {
          const officeType = item.querySelector('.timing-office-type');
          const time = item.querySelector('.hours-time');
          const weeklyOff = item.querySelector('.hours-weekly-off');
          
          const officeTypeText = officeType ? getElementText(officeType) : '';
          const timeText = time ? getElementText(time) : '';
          const weeklyOffText = weeklyOff ? getElementText(weeklyOff) : '';
          
          return [officeTypeText, timeText, weeklyOffText].filter(t => t).join('. ');
        }).filter(t => t).join('. ');
        
        const hoursNote = hoursSection.querySelector('.hours-note');
        const noteText = hoursNote ? getElementText(hoursNote) : '';
        
        const hoursParts = [];
        if (titleText) hoursParts.push(titleText);
        if (timingsText) hoursParts.push(timingsText);
        if (noteText) hoursParts.push(noteText);
        
        if (hoursParts.length > 0) {
          pageText += (pageText ? '. ' : '') + hoursParts.join('. ');
        }
      }
    });
    
    if (pageText.trim()) {
      sections.push(isMarathi() ? 'पृष्ठ मजकूर' : 'Page Content');
      sections.push(pageText.trim());
    }
  }

  // Read footer sections
  const footerSections = [
    { attr: 'footer-other-links', label: isMarathi() ? 'इतर लिंक्स्' : 'Other Links' },
    { attr: 'footer-quick-links', label: isMarathi() ? 'उपयुक्त लिंक्स्' : 'Quick Links' },
    { attr: 'footer-social-media', label: isMarathi() ? 'सामाजिक माध्यमे' : 'Social Media' },
    { attr: 'footer-visitor-count', label: isMarathi() ? 'भेटींची संख्या' : 'Visitor Count' },
    { attr: 'footer-last-updated', label: isMarathi() ? 'वेबसाइट अंतिम अपडेट' : 'Website Last Updated' },
  ];

  footerSections.forEach(({ attr, label }) => {
    const selector = `[data-read-section="${attr}"]`;
    const element = document.querySelector(selector);
    
    if (element) {
      let text = '';
      
      if (attr === 'footer-other-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-quick-links') {
        const title = element.querySelector('.footer-subtitle');
        const links = element.querySelectorAll('.footer-link');
        const titleText = title ? getElementText(title) : '';
        const linksText = Array.from(links)
          .map(t => getElementText(t))
          .filter(t => t.trim())
          .join(', ');
        text = [titleText, linksText].filter(t => t).join('. ');
      } else if (attr === 'footer-social-media') {
        const title = element.querySelector('.footer-subtitle');
        const description = element.querySelector('.social-description');
        const titleText = title ? getElementText(title) : '';
        const descText = description ? getElementText(description) : '';
        text = [titleText, descText].filter(t => t).join('. ');
      } else if (attr === 'footer-visitor-count') {
        const statItems = element.querySelectorAll('.stat-item');
        const statsText = Array.from(statItems).map(item => {
          const number = item.querySelector('.stat-number');
          const label = item.querySelector('.stat-label');
          const numText = number ? getElementText(number) : '';
          const labelText = label ? getElementText(label) : '';
          return numText && labelText ? `${labelText}: ${numText}` : '';
        }).filter(t => t).join('. ');
        text = statsText;
      } else if (attr === 'footer-last-updated') {
        text = getElementText(element);
      }

      if (text.trim()) {
        sections.push(label);
        sections.push(text.trim());
      }
    }
  });

  return sections.filter(section => section.trim()).join('. ');
};

export const usePageReader = () => {
  const { isMarathi } = useLanguage();
  const location = useLocation();
  const [isReading, setIsReading] = useState(false);
  const synthRef = useRef(null);
  const utteranceRef = useRef(null);
  const voicesLoadedRef = useRef(false);

  // Load voices properly
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
      
      const loadVoices = () => {
        const voices = synthRef.current.getVoices();
        if (voices.length > 0) {
          voicesLoadedRef.current = true;
          console.log('Available voices:', voices.map(v => ({ name: v.name, lang: v.lang })));
        }
      };

      // Try to load voices immediately
      loadVoices();
      
      // Some browsers need this event
      if (!voicesLoadedRef.current) {
        synthRef.current.onvoiceschanged = loadVoices;
      }
    }
  }, []);

  const stopReading = useCallback(() => {
    if (!synthRef.current) return;
    synthRef.current.cancel();
    setIsReading(false);
    if (utteranceRef.current) {
      utteranceRef.current = null;
    }
  }, []);

  const startReading = useCallback(() => {
    // Check which page we're on
    const currentPath = location.pathname;
    
    if (currentPath !== '/' && currentPath !== '/overview' && currentPath !== '/administrative-structure' && currentPath !== '/board-project' && currentPath !== '/board-members' && currentPath !== '/annual-report' && currentPath !== '/circulars' && currentPath !== '/workers-welfare' && currentPath !== '/right-to-info' && currentPath !== '/right-to-service' && currentPath !== '/tenders-notices' && currentPath !== '/list-of-services' && currentPath !== '/feedback' && currentPath !== '/grievance' && currentPath !== '/faq' && currentPath !== '/100-days-program' && currentPath !== '/contact' && currentPath !== '/sitemap' && currentPath !== '/privacy' && currentPath !== '/screen-reader' && currentPath !== '/contact-sop') {
      alert(
        isMarathi()
          ? 'स्क्रीन रीडर फक्त समर्थित पृष्ठांवर कार्य करते.'
          : 'Screen reader only works on supported pages.'
      );
      return;
    }

    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      alert(
        isMarathi()
          ? 'आपल्या ब्राउझरमध्ये स्क्रीन रीडर आवाज समर्थन उपलब्ध नाही.'
          : 'Your browser does not support speech synthesis for screen reading.'
      );
      return;
    }

    // Function to actually start speaking
    const speakText = () => {
      // Get content based on current page
      let text = '';
      if (currentPath === '/') {
        text = getHomePageContent(isMarathi);
      } else if (currentPath === '/overview') {
        text = getOverviewPageContent(isMarathi);
      } else if (currentPath === '/administrative-structure') {
        text = getAdministrativeStructureContent(isMarathi);
      } else if (currentPath === '/board-project') {
        text = getBoardProjectContent(isMarathi);
      } else if (currentPath === '/board-members') {
        text = getBoardMembersContent(isMarathi);
      } else if (currentPath === '/annual-report') {
        text = getAnnualReportContent(isMarathi);
      } else if (currentPath === '/circulars') {
        text = getCircularsContent(isMarathi);
      } else if (currentPath === '/workers-welfare') {
        text = getWorkersWelfareContent(isMarathi);
      } else if (currentPath === '/right-to-info') {
        text = getRightToInfoContent(isMarathi);
      } else if (currentPath === '/right-to-service') {
        text = getRightToServiceContent(isMarathi);
      } else if (currentPath === '/tenders-notices') {
        text = getTendersNoticesContent(isMarathi);
      } else if (currentPath === '/list-of-services') {
        text = getListOfServicesContent(isMarathi);
      } else if (currentPath === '/feedback') {
        text = getFeedbackContent(isMarathi);
      } else if (currentPath === '/grievance') {
        text = getGrievanceRedressalContent(isMarathi);
      } else if (currentPath === '/faq') {
        text = getFAQContent(isMarathi);
      } else if (currentPath === '/100-days-program') {
        text = get100DaysProgramContent(isMarathi);
      } else if (currentPath === '/contact') {
        text = getContactUsContent(isMarathi);
      } else if (currentPath === '/sitemap') {
        text = getSiteMapContent(isMarathi);
      } else if (currentPath === '/privacy') {
        text = getPrivacyPolicyContent(isMarathi);
      } else if (currentPath === '/screen-reader') {
        text = getScreenReaderContent(isMarathi);
      } else if (currentPath === '/contact-sop') {
        text = getContactSOPContent(isMarathi);
      }
      
      if (!text.trim()) {
        alert(
          isMarathi()
            ? 'वाचण्यासाठी मजकूर उपलब्ध नाही.'
            : 'No content available to read.'
        );
        return;
      }

      const utterance = new SpeechSynthesisUtterance(text);
      const langCode = isMarathi() ? 'mr-IN' : 'en-IN';
      utterance.lang = langCode;
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;

      const synth = synthRef.current || window.speechSynthesis;
      
      // Get available voices - ensure they're loaded
      let voices = synth.getVoices();
      if (voices.length === 0) {
        // Wait a bit more for voices to load
        setTimeout(() => {
          voices = synth.getVoices();
          selectAndSpeak(voices, utterance, synth, langCode);
        }, 200);
        return;
      }

      selectAndSpeak(voices, utterance, synth, langCode);
    };

    // Function to select voice and speak
    const selectAndSpeak = (voices, utterance, synth, langCode) => {
      // Log available voices for debugging
      const marathiVoices = voices.filter(v => v.lang === langCode || v.lang.startsWith(isMarathi() ? 'mr' : 'en'));
      console.log('Looking for voice with lang:', langCode);
      console.log('Available matching voices:', marathiVoices.map(v => ({ name: v.name, lang: v.lang })));

      // Try to find appropriate voice
      let voice = voices.find((v) => v.lang === langCode);
      
      if (!voice && isMarathi()) {
        // Try alternative Marathi language codes
        voice = voices.find((v) => v.lang === 'mr') ||
                voices.find((v) => v.lang.startsWith('mr-')) ||
                voices.find((v) => v.name.toLowerCase().includes('marathi')) ||
                voices.find((v) => v.name.toLowerCase().includes('hindi')); // Hindi might work for Marathi
      }
      
      if (!voice && !isMarathi()) {
        // For English, try to find any English voice
        voice = voices.find((v) => v.lang.startsWith('en'));
      }

      // If still no voice found, use default (browser will choose)
      if (voice) {
        utterance.voice = voice;
        console.log('Using voice:', voice.name, voice.lang);
      } else {
        console.warn('No matching voice found, using browser default');
        // Still try to speak - browser might have a default
        if (isMarathi()) {
          // Warn user that Marathi voice might not be available
          console.warn('Marathi voice not found. Speech may use default voice.');
        }
      }

      // Set up event handlers
      utterance.onend = () => {
        setIsReading(false);
        utteranceRef.current = null;
      };
      
      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event.error);
        setIsReading(false);
        utteranceRef.current = null;
        
        // Provide user feedback
        if (event.error === 'not-allowed' || event.error === 'network') {
          alert(
            isMarathi()
              ? 'आवाज वाचनासाठी परवानगी आवश्यक आहे किंवा नेटवर्क समस्या आहे.'
              : 'Permission required for voice reading or network issue.'
          );
        } else if (isMarathi() && !voice) {
          alert(
            'मराठी आवाज उपलब्ध नाही. कृपया आपल्या ऑपरेटिंग सिस्टममध्ये मराठी आवाज इन्स्टॉल करा किंवा इंग्रजी भाषा वापरा.'
          );
        }
      };

      synth.cancel(); // Cancel any ongoing speech
      synth.speak(utterance);
      setIsReading(true);
      utteranceRef.current = utterance;
    };

    // Wait for DOM and voices to be ready
    setTimeout(() => {
      speakText();
    }, 100);
  }, [isMarathi, location.pathname]);

  const toggleReading = useCallback(() => {
    if (isReading) {
      stopReading();
    } else {
      startReading();
    }
  }, [isReading, startReading, stopReading]);

  return { isReading, toggleReading, stopReading };
};

